import { skipToken, useQuery } from '@tanstack/react-query';

import { Importer } from '@air/api';
import { type Succeeded } from '@air/server-sdk';
import { callAirApi } from '@air/utils-api';

import { UPLOADED_BATCH_OFFER_VISIBLE_MS } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/isUploadedBatchOfferVisible';

export const PRIVATE_IMPORTER_JOB_ITEMS_LIST_KEY = 'PRIVATE_IMPORTER_JOB_ITEMS_LIST';

/** The endpoint's page cap. */
const IMPORTER_JOB_ITEMS_PAGE_LIMIT = 1_000;

const IMPORT_STILL_RUNNING_RETRY_DELAY_MS = 2_000;

/** Keeps polling a lagging import for as long as the offer it feeds can still show. */
const IMPORT_STILL_RUNNING_MAX_RETRIES = Math.ceil(
  UPLOADED_BATCH_OFFER_VISIBLE_MS / IMPORT_STILL_RUNNING_RETRY_DELAY_MS,
);

class ImportStillRunningError extends Error {
  constructor() {
    super('Import is still running');
    this.name = 'ImportStillRunningError';
  }
}

export type PrivateImporterJobItem = Succeeded<typeof Importer.listJobItems>['data'][number];

export type PrivateImporterJobItemsListParams = Omit<
  NonNullable<Parameters<typeof Importer.listJobItems>[0]>,
  'cursor' | 'importId' | 'limit' | 'workspaceId'
> & {
  importId: string | undefined;
  workspaceId: string | undefined;
};

export type PrivateImporterJobItemsListKey = [
  typeof PRIVATE_IMPORTER_JOB_ITEMS_LIST_KEY,
  PrivateImporterJobItemsListParams,
];

export const getPrivateImporterJobItemsListKey = (
  params: PrivateImporterJobItemsListParams,
): PrivateImporterJobItemsListKey => [PRIVATE_IMPORTER_JOB_ITEMS_LIST_KEY, params];

export type UsePrivateImporterJobItemsListOptions = {
  enabled?: boolean;
};

export const fetchPrivateImporterJobItemsList = async ({
  importId,
  type,
  workspaceId,
}: PrivateImporterJobItemsListParams & { importId: string; workspaceId: string }) => {
  const items: PrivateImporterJobItem[] = [];
  let cursor: string | undefined;

  do {
    const page = await callAirApi(() =>
      Importer.listJobItems({
        cursor,
        importId,
        limit: IMPORTER_JOB_ITEMS_PAGE_LIMIT,
        type,
        workspaceId,
      }),
    );

    // The list can miss files the job is still writing, so a partial read must fail and retry.
    if (!page.complete) {
      throw new ImportStillRunningError();
    }

    items.push(...page.data);
    cursor = page.pagination.hasMore ? (page.pagination.cursor ?? undefined) : undefined;
  } while (cursor);

  return items;
};

/** Pages `Importer.listJobItems` through every item the import created. */
export const usePrivateImporterJobItemsList = (
  params: PrivateImporterJobItemsListParams,
  options?: UsePrivateImporterJobItemsListOptions,
) => {
  const { importId, workspaceId } = params;

  return useQuery({
    queryKey: getPrivateImporterJobItemsListKey(params),
    queryFn:
      importId && workspaceId
        ? () => fetchPrivateImporterJobItemsList({ ...params, importId, workspaceId })
        : skipToken,
    enabled: !!importId && !!workspaceId && (options?.enabled ?? true),
    // A finished import's items never change, and a refetch re-pages the whole list.
    staleTime: Infinity,
    // Any other failure (NOT_FOUND, NO_PERMISSION, ...) will not fix itself, and the offer just stays down.
    retry: (failureCount, error) =>
      error instanceof ImportStillRunningError && failureCount < IMPORT_STILL_RUNNING_MAX_RETRIES,
    retryDelay: IMPORT_STILL_RUNNING_RETRY_DELAY_MS,
  });
};
